import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, from, map, mergeMap, shareReplay, tap, throwError, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class RequestFormService {
  private darkModeKey = 'darkModePreference';
  private darkMode = true;
  themeChanged = new EventEmitter<boolean>();
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly basePcUrl = environment.apiBasePcUrl;
 private selectedImoSource = new BehaviorSubject<string | null>(null);
  selectedImo$ = this.selectedImoSource.asObservable();

  setSelectedImo(imo: string) {
    this.selectedImoSource.next(imo);
  }
  constructor(private http: HttpClient) {
    const storedPreference = localStorage.getItem(this.darkModeKey);
    if (storedPreference) {
      this.darkMode = JSON.parse(storedPreference);
    }
  }
  getClientList(): Observable<any> {
    const url = `${this.baseUrl}/scperformance/client`;
    return this.http.get<any>(url);
  }
  quickOnboardingByImo(imo: number): Observable<any> {
    const url = `${this.basePcUrl}/port-consumption/quick-onboarding`;
    return this.http.post<any>(url, { imo_number: imo });
  }

  getVesselList(): Observable<any> {
    const url = `${this.basePcUrl}/port-consumption/q88/`;
    return this.http.get<any>(url);
  }

  getUserDetails(): Observable<string> {
    const customHeader = { responseType: 'text' as 'json' };
    const url = `${this.baseUrl}/auth`;
    return this.http
      .get<string>(url, customHeader)
      .pipe(catchError(this.errorHandler));
  }
  getCombinedExcelData(source: string): Observable<string> {
    const url = `${this.baseUrl}/combined_get_data`;
    return this.http
      .get(url, {
        params: { source },
        responseType: 'text',
      })
      .pipe(catchError(this.errorHandler));
  }
  postRequestForm(
    jsonData: any,
    jsonString: any,
    thirdParam?: File[] | any, // 👈 can be files OR payload
  ) {
    const formData = new FormData();

    // always
    formData.append('json_data', JSON.stringify(jsonData));
    formData.append('json_string', JSON.stringify(jsonString));

    // CASE 1: files uploaded
    if (Array.isArray(thirdParam)) {
      thirdParam.forEach((file: File, index: number) => {
        formData.append(`document${index + 1}`, file, file.name);
      });
    }
    // CASE 2: json payload (no file)
    else if (thirdParam) {
      formData.append('meta_data', JSON.stringify(thirdParam));
    }

    return this.http
      .post(`${this.baseUrl}/scperformance/generate_report`, formData)
      .pipe(catchError(this.errorHandler));
  }

  postFormDetails(jsonData: any, file?: File) {
    const formData = new FormData();
    formData.append('json_data', JSON.stringify(jsonData));
    if (file) {
      formData.append('files', file, file.name);
    }
    const url = `${this.baseUrl}/scperformance/save_submit`;
    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }
  //  postOnboardingFormDetails(jsonData: any, file?: File) {
  //   const formData = new FormData();
  //   formData.append('json_data', JSON.stringify(jsonData));
  //   if (file) {
  //     formData.append('files', file, file.name);
  //   }
  //   // replace URL
  //    const url = `${this.baseUrl}/`;
  //   return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  // }
  //   postOnboardingFormDetails(jsonData: any, file?: File) {
  //   const formData = new FormData();

  //   formData.append('json_data', JSON.stringify(jsonData));

  //   if (file) {
  //     formData.append('files', file, file.name);
  //   }

  //   const url = `${this.baseUrl}/scperformance/onboarding/submit`;

  //   return this.http.post(url, formData).pipe(
  //     catchError(this.errorHandler)
  //   );
  // }

  postOnboardingJson(payload: any) {
    return this.submitOnboarding(payload);
  }

  postOnboardingFormDetails(jsonData: any, file?: File) {
    return this.submitOnboarding(jsonData, file);
  }

  /**
   * POST /scperformance/onboarding/submit
   * Unified onboarding submission for all modules.
   */
  submitOnboarding(
    payload: Record<string, unknown>,
    file?: File,
  ): Observable<unknown> {
    const url = `${this.baseUrl}/scperformance/onboarding/submit`;
    const isQuick = payload['quick_onboarding'] === true;
    const useJsonBody = isQuick || !file;

    if (useJsonBody) {
      return this.http
        .post(url, payload, {
          headers: { 'Content-Type': 'application/json' },
        })
        .pipe(catchError(this.errorHandler));
    }

    const formData = new FormData();
    const serialized = JSON.stringify(payload);
    formData.append('json_data', serialized);
    formData.append('data', serialized);
    if (file) {
      formData.append('files', file, file.name);
    }

    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }

  /** @deprecated Use submitOnboarding — kept for existing call sites. */
  submitSailingPerformanceOnboarding(
    payload: Record<string, unknown>,
    file?: File,
  ): Observable<unknown> {
    return this.submitOnboarding(payload, file);
  }

  getOnboardingVesselDetails(imo: number): Observable<any> {
    const url = `${this.baseUrl}/scperformance/onboarding/vessel-details?imo=${imo}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getVesselTcpSpeedConsumption(imo: any, clientName?: string): Observable<any> {
    let url = `${this.baseUrl}/scperformance/vessel_tcp_speed_consumption?imo=${imo}`;
    if (clientName) {
      url += `&client=${encodeURIComponent(clientName)}`;
    }
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getVesselCpLimits(imo: any, clientName: string): Observable<any> {
    const url = `${this.baseUrl}/scperformance/vessel_cp_limits?imo=${imo}&client_name=${encodeURIComponent(clientName)}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  saveVesselCpLimits(payload: any): Observable<any> {
    const url = `${this.baseUrl}/scperformance/vessel_cp_limits`;
    return this.http.post<any>(url, payload).pipe(catchError(this.errorHandler));
  }

  updateVesselTcpSpeedConsumptionPoint(payload: any): Observable<any> {
    const url = `${this.baseUrl}/scperformance/vessel_tcp_speed_consumption/update_point`;
    return this.http.post<any>(url, payload).pipe(catchError(this.errorHandler));
  }

  updateVesselTcpSpeedConsumptionBulk(payload: any): Observable<any> {
    const url = `${this.baseUrl}/scperformance/vessel_tcp_speed_consumption/update_bulk`;
    return this.http.post<any>(url, payload).pipe(catchError(this.errorHandler));
  }

  getReports() {
    const url = `${this.baseUrl}/scperformance/retrive_report`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getRouteData(requestUrl: string) {
    const url = encodeURI(decodeURIComponent(requestUrl)); // Fix double encoding issues

    const httpOptions = {
      responseType: 'text' as 'json', // Ensure response is text
      headers: new HttpHeaders({}), // Explicitly set empty headers
    };

    return this.http.get(url, httpOptions).pipe(
      tap((res) => console.log('Response:', res)), // Log response
      catchError((error) => {
        return throwError(error);
      }),
    );
  }

  getErrorMessage() {
    const url = `${this.baseUrl}/scperformance/error_table`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getUserManual() {
    const url = `${this.baseUrl}/scperformance/download_user_manual`;
    return this.http
      .get(url, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler));
  }
  getUploadedMainCp(id: any, client: any) {
    const url = `${this.baseUrl}/scperformance/downloadmaincp?uniquepk=${id}&client=${client}`;
    return this.http
      .get(url, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler));
  }

  deleteRow(id: any): Observable<any> {
    const url = `${this.baseUrl}/scperformance/delete_records?uniquePk=${id}`;
    return this.http.post(url, {}).pipe(catchError(this.errorHandler));
  }
  uploadPeriodicReport(files: File[]): Observable<any> {
    const url = `${this.baseUrl}/scperformance/generate_periodic_report`;
    const formData: FormData = new FormData();

    files.forEach((file, index) => {
      formData.append(`document${index + 1}`, file, file.name);
    });

    return this.http.post(url, formData);
  }

  uploadFiles(
    options:
      | { files: File[] }
      | { imo_number: number; start_date: string; end_date: string },
  ): Observable<any> {
    const url = `${this.baseUrl}/scperformance/generate_voyage_config`;

    // 🔹 CASE 1: Excel upload
    if ('files' in options) {
      const formData = new FormData();
      options.files.forEach((file, index) => {
        formData.append(`document${index + 1}`, file, file.name);
      });

      return this.http.post(url, formData);
    }

    // 🔹 CASE 2: JSON input
    return this.http.post(url, {
      imo_number: options.imo_number,
      start_date: options.start_date,
      end_date: options.end_date,
    });
  }

  getRoutePreview(
    options: { files: File[], report_type?: string, imo_no?: string, start_date?: string, end_date?: string }
  ): Observable<any> {
    const url = `${this.baseUrl}/scperformance/route_preview`;
    const formData = new FormData();
    options.files.forEach((file, index) => {
      formData.append(`document${index + 1}`, file, file.name);
    });
    if (options.report_type) {
      formData.append('report_type', options.report_type);
    }
    if (options.imo_no) {
      formData.append('imo_no', options.imo_no);
    }
    if (options.start_date) {
      formData.append('start_date', options.start_date);
    }
    if (options.end_date) {
      formData.append('end_date', options.end_date);
    }
    return this.http.post(url, formData);
  }

  saveRoute(routeId: string, routeGeoJson: any): Observable<any> {
    const url = `${this.baseUrl}/scperformance/save_route`;
    return this.http.post(url, {
      route_id: routeId,
      route: routeGeoJson
    });
  }

  recalculateRoute(waypoints: any[]): Observable<any> {
    const url = `${this.baseUrl}/scperformance/recalculate_route`;
    return this.http.post(url, { waypoints });
  }

  uploadFile(file: File): Observable<any> {
    const url = `${this.baseUrl}/scperformance/master_daily_report_eiger`;
    const formData: FormData = new FormData();

    // Append the single file with the key 'file'
    formData.append('file', file, file.name);

    return this.http.post(url, formData);
  }
  updatePortData(jsonData: any): Observable<any> {
    const formData = new FormData();
    formData.append('json_data', JSON.stringify(jsonData));

    const url = `${this.baseUrl}/scperformance/update_port_db`;

    return this.http
      .post(url, formData, { responseType: 'text' })
      .pipe(catchError(this.errorHandler));
  }
  // updateClientData(jsonData: any): Observable<any> {
  //   const url = `${this.baseUrl}/scperformance/client`;
  //  return this.http.post(url, jsonData);
  // }
  updateClientData(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/scperformance/client`;
    return this.http.post(url, jsonData);
  }
  // getClientList(): Observable<any> {
  //   const url = `${this.baseUrl}/scperformance/client`;
  //   return this.http.get(url);
  // }

  postPeriodicForm(jsonData: any, json_string: any, files: File[]) {
    const formData = new FormData();
    formData.append('json_data_periodic', JSON.stringify(jsonData));
    formData.append('json_string', JSON.stringify(json_string));
    files.forEach((file, index) => {
      formData.append(`document${index + 1}`, file, file.name);
    });

    const url = `${this.baseUrl}/scperformance/periodic_report_inputs`;
    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }
  submitPeriodicForm(json_string: any) {
    const formData = new FormData();
    formData.append('json_data_periodic', JSON.stringify(json_string));

    const url = `${this.baseUrl}/scperformance/periodic_report_form`;
    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }
  getPeriodicReports() {
    const url = `${this.baseUrl}/scperformance/get_generated_periodic_pdf`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  generatePeriodicReport(jsonData: any, file?: File, formId?: string) {
    const formData = new FormData();
    formData.append('payload', JSON.stringify(jsonData));
    if (formId) {
      formData.append('form_id', formId);
    }
    if (file) {
      formData.append('file', file, file.name);
    }
    const url = `${this.baseUrl}/scperformance/periodic-report`;
    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }

  downloadPeriodicReport(s3_link: any) {
    const url = `${this.baseUrl}/scperformance/downloadperiodicpdf`;
    return this.http
      .post(url, { s3_link }, { responseType: 'blob' })
      .pipe(catchError(this.errorHandler));
  }
  downloadPeriodicSummary(
    pdfFileName: string,
    client: string,
  ): Observable<Blob> {
    const url = `${this.baseUrl}/scperformance/download_periodic_summary_pdf?pdffilename=${pdfFileName}&client=${client}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  getFormDetails(ship: string, imoNumber: string) {
    const url = `${this.baseUrl}/scperformance/retrieve_single_report?imoNumber=${imoNumber}&ship=${ship}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  initialRouteAdvice(requestData: any) {
    const url = `${this.baseUrl}/scperformance/initial_route_advice`;

    const formData = new FormData();
    formData.append('json_data', JSON.stringify(requestData));

    return this.http
      .post(url, formData, { responseType: 'blob' })
      .pipe(catchError(this.errorHandler));
  }
  getPortDetails(): Observable<any> {
    const url = `${this.baseUrl}/scperformance/port_details`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  private cachedDtnPorts$: Observable<any> | null = null;

  getPortDetailsDtn(): Observable<any> {
    if (!this.cachedDtnPorts$) {
      const url = `${this.baseUrl}/scperformance/port_details_dtn`;
      this.cachedDtnPorts$ = this.http.get<any>(url).pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.cachedDtnPorts$;
  }

  getNotifications() {
    const url = `${this.baseUrl}/scperformance/get-total-record`;
    return this.http
      .get<any>(url, {
        headers: { 'X-Skip-Ngx-Ui-Loader': 'true' },
      })
      .pipe(catchError(this.errorHandler));
  }
  updateNotificationStatus(body: any) {
    const url = `${this.baseUrl}/scperformance/update_notification_status`;

    return this.http
      .post(url, body, {
        headers: { 'X-Skip-Ngx-Ui-Loader': 'true' },
      })
      .pipe(catchError(this.errorHandler));
  }
  errorHandler(error: HttpErrorResponse) {
    return throwError(() => error || 'Server Error');
  }
  toggleDarkMode() {
    // Toggle theme preference
    this.darkMode = !this.darkMode;
    // Update local storage
    localStorage.setItem(this.darkModeKey, JSON.stringify(this.darkMode));
    this.themeChanged.emit(this.darkMode);
  }
  getVesselFinderList() {
    const url = `${this.baseUrl}/scperformance/vessel-finder-list`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getkplerList(imoNumbers: string[]) {
    const imoParam = imoNumbers.join(',');

    const url = `${this.baseUrl}/scperformance/vessel-live-tracking?imo=${imoParam}`;

    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getInitialReport(s3_path: string) {
    const url = `${this.baseUrl}/scperformance/download-initial-report?s3_path=${encodeURIComponent(s3_path)}`;

    return this.http
      .get(url, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler));
  }
  getRouteCompare() {
    const url = `${this.baseUrl}/scperformance/compare_route`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getDeviationNotifications(userName: any) {
    const encodedUsername = encodeURIComponent(userName);
    const url = `${this.baseUrl}/scperformance/deviation-notifications?username=${encodedUsername}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }
  getVesselsLatestRob(client?: string, username?: string, subusername?: string): Observable<any> {
    let queryParams: string[] = [];
    if (client && client !== 'all') {
      queryParams.push(`client=${encodeURIComponent(client)}`);
    }
    if (username) {
      queryParams.push(`username=${encodeURIComponent(username)}`);
    }
    if (subusername) {
      queryParams.push(`subusername=${encodeURIComponent(subusername)}`);
    }
    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

    const scUrl = `${this.baseUrl}/scperformance/vessels-latest-rob${queryString}`;
    const pcUrl = `${this.basePcUrl}/port-consumption/vessels-latest-rob${queryString}`;

    return this.http
      .get<any>(scUrl, {
        headers: { 'X-Skip-Ngx-Ui-Loader': 'true' },
      })
      .pipe(
        catchError(() => {
          return this.http.get<any>(pcUrl, {
            headers: { 'X-Skip-Ngx-Ui-Loader': 'true' },
          });
        }),
        catchError(this.errorHandler)
      );
  }
  updateDeviationNotificationPopup(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/scperformance/mark-deviation-read`;
    return this.http.post(url, jsonData);
  }
  updateSpeedNotificationPopup(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/scperformance/mark-speed-read`;
    return this.http.post(url, jsonData);
  }
  muteVesselAlert(jsonData: { imo: number }): Observable<any> {
    const url = `${this.baseUrl}/scperformance/mute-email-alert`;
    return this.http.post(url, jsonData);
  }
  getThresholdSpeed(userName: any) {
    const encodedUsername = encodeURIComponent(userName);
    const url = `${this.baseUrl}/scperformance/get-threshold-speed?username=${encodedUsername}`;

    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  updatedAlerts(jsonData: any): Observable<any> {
    const url = `${this.baseUrl}/scperformance/update-threshold-alerts`;
    return this.http.post(url, jsonData);
  }
  getPerformanceCalc(imo: any) {
    const url = `${this.baseUrl}/scperformance/live-performance-data?imo=${imo}`;

    return this.http.get(url, { responseType: 'text' }).pipe(
      map((res: any) => {
        // replace invalid JSON values
        const fixed = res.replace(/Infinity/g, 'null');
        return JSON.parse(fixed);
      }),
      catchError(this.errorHandler),
    );
  }
  getFleetEmissionSummary(year: number, client?: string): Observable<any> {
    let url = `${this.basePcUrl}/v1/emission/fleet-summary?year=${year}`;
    if (client && client !== 'ALL') {
      url += `&client=${encodeURIComponent(client)}`;
    }
    const headers = new HttpHeaders({ 'X-Skip-Ngx-Ui-Loader': 'true' });
    return this.http
      .get<any>(url, { headers })
      .pipe(catchError(this.errorHandler));
  }

  simulationReport(jsonData: any, files: File | File[], waypoints?: any[] | any[][]): Observable<any> {
    const url = `${this.baseUrl}/scperformance/simulation_report`;

    const formData = new FormData();
    formData.append('json_data', JSON.stringify(jsonData));

    // Append each waypoint array as a repeated 'waypoints' key (no index suffix)
    if (Array.isArray(waypoints)) {
      if (waypoints.length > 0 && Array.isArray(waypoints[0])) {
        // Multi-route: waypoints is an array of arrays
        (waypoints as any[][]).forEach((routeWaypoints) => {
          formData.append('waypoints', JSON.stringify(routeWaypoints));
        });
      } else {
        // Single route: waypoints is a flat array of points
        formData.append('waypoints', JSON.stringify(waypoints));
      }
    }

    // Append each file as a repeated 'files' key (no index suffix)
    if (Array.isArray(files)) {
      files.forEach((f) => formData.append('files', f, f.name));
    } else if (files) {
      formData.append('files', files, (files as File).name);
    }

    return this.http
      .post(url, formData, { responseType: 'blob' })
      .pipe(
        catchError((err: any) => {
          if (err?.error instanceof Blob) {
            return from(err.error.text() as Promise<string>).pipe(
              mergeMap((text: string) => {
                try {
                  const parsed = JSON.parse(text);
                  const errorMsg = parsed.detail || parsed.details || parsed.error || parsed.message || text;
                  const customErr = new Error(errorMsg);
                  (customErr as any).error = parsed;
                  return throwError(() => customErr);
                } catch {
                  return throwError(() => new Error(text || err.message));
                }
              })
            );
          }
          return throwError(() => err);
        })
      );
  }

  csvRoute(
    imoNo: string,
    vesselName: string,
    file: File,
    waypoints: any[],
  ): Observable<any> {
    const url = `${this.baseUrl}/scperformance/save-csv-ordinate`;

    const formData = new FormData();

    formData.append('file', file);

    formData.append('imo_no', imoNo);

    formData.append('vessel_name', vesselName);

    // Send waypoints JSON string
    formData.append('waypoints', JSON.stringify(waypoints));

    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }
  getForecastSource(imo: any, vesselName: any) {
    const url = `${this.baseUrl}/scperformance/get-forecast-source`;

    const params = {
      imo: imo,
      vessel_name: vesselName,
    };

    return this.http.get(url, { params }).pipe(catchError(this.errorHandler));
  }

  updateForecastSource(payload: any) {
    const url = `${this.baseUrl}/scperformance/update-forecast-source`;
    return this.http.post(url, payload).pipe(catchError(this.errorHandler));
  }

  getCsvRoute(imoNumber: any) {
    const url = `${this.baseUrl}/scperformance/get-csv-route?imo_no=${imoNumber}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler));
  }

  getCsvWaypoints(file: File) {
    const url = `${this.baseUrl}/scperformance/get-csv-waypoints`;

    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(url, formData).pipe(catchError(this.errorHandler));
  }
  getAisVessels() {
  const url = `${this.baseUrl}/scperformance/get-ais-vessels`;
  return this.http.get<any>(url).pipe(
    catchError(this.errorHandler)
  );
}

addAisVessel(payload: any) {
  const url = `${this.baseUrl}/scperformance/add-ais-vessel`;
  return this.http.post<any>(url, payload).pipe(
    catchError((error) => this.errorHandler(error))
  );
}

deleteAisVessel(imo: any) {
  const url = `${this.baseUrl}/scperformance/delete-ais-vessel?imo=${imo}`;
  return this.http.delete<any>(url).pipe(
    catchError((error) => this.errorHandler(error))
  );
}

  /** Vessel insights AI — scperformance on speed & consumption (apps) API. */
  uploadVesselInsights(image: File, prompt: string): Observable<any> {
    const isImage =
      image.type.startsWith('image/') ||
      /\.(png|jpe?g|webp|gif|bmp)$/i.test(image.name);
    if (!isImage || !image.size) {
      throw new Error('Vessel insights requires an image file, not a PDF.');
    }
    const url = `${this.baseUrl}/scperformance/vessel-insights`;
    const formData = new FormData();
    const fileName = image.name || 'port-consumption-report.jpg';
    const headers = new HttpHeaders({ 'X-Skip-Ngx-Ui-Loader': 'true' });
    // Backend expects multipart field name "file" (returns "File is required" otherwise).
    formData.append('file', image, fileName);
    formData.append('prompt', prompt);
    return this.http
      .post<any>(url, formData, { headers })
      .pipe(
        timeout(90000),
        catchError(this.errorHandler),
      );
  }

  postFixedEta(jsonData: any, file?: File) {
  const url = `${this.baseUrl}/scperformance/fixed_eta`;
    const formData = new FormData();
    formData.append('json_data', JSON.stringify(jsonData));
    if (file) {
      formData.append('file', file);
    }
    return this.http.post(url, formData, { responseType: 'blob' }).pipe(catchError(this.errorHandler));
  }

  getAisTrack(imo: string) {
  const url = `${this.baseUrl}/scperformance/get-ais-track/${imo}`;

  return this.http
    .get<any>(url)
    .pipe(catchError(this.errorHandler));
}
  isDarkMode() {
    // Return theme preference
    return this.darkMode;
  }
}
